/**
 * Suno Onboarding Module
 *
 * Provides a "Connect To Suno" button on the drop modal and an onboarding popup
 * that guides users through extension installation and Suno login.
 *
 * State machine: install → connect → success → auto-open Suno Manager
 *
 * Auto-detection:
 * - Listens for `showlarkExtensionReady` (content script loaded after install)
 * - Listens via `sunoService.onConnectionStatusChange()` (token becomes available)
 * - Transitions popup state automatically when extension/token detected
 */

import { sunoService } from '../lib/suno/suno-service.js';
import { openSunoManager } from './suno-manager.js';
import { hideDropModal } from './overlay-panels.js';
import { lockBodyScroll, unlockBodyScroll } from '../lib/overflow-lock.js';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/showlark-suno-extension/pfebnechfojincacjjodikikmndpdglm';
const SUNO_URL = 'https://suno.com';
const SUCCESS_DELAY_MS = 1200;

// Module state
let popupEl = null;
let currentState = null; // 'install' | 'connect' | 'success' | null
let cleanupFns = [];
let isPopupCreated = false;

// ─── Public API ──────────────────────────────────────────

/**
 * Initialize the onboarding module.
 * Called once from init.js after DOM is ready.
 */
export function initSunoOnboarding() {
  const btn = document.getElementById('connectToSunoBtn');
  if (!btn) return;

  btn.addEventListener('click', handleConnectClick);

  // Permanent listeners — intentionally never removed.
  // These keep the button text in sync for the entire page lifetime
  // (e.g., "Connect To Suno" → "Open Suno Library" when token is acquired).
  document.addEventListener('showlarkExtensionReady', () => updateButtonState());
  sunoService.onConnectionStatusChange(() => updateButtonState());

  // Set initial button state
  updateButtonState();
}

// ─── Button Click Handler ────────────────────────────────

async function handleConnectClick() {
  const extensionAvailable = await sunoService.isExtensionAvailable();

  if (!extensionAvailable) {
    showPopup('install');
    return;
  }

  const tokenStatus = await sunoService.getTokenStatus();
  if (tokenStatus.hasToken) {
    // Extension + token → go directly to Suno Manager
    hideDropModal();
    openSunoManager('library');
  } else {
    showPopup('connect');
  }
}

// ─── Button State ────────────────────────────────────────

async function updateButtonState() {
  const btn = document.getElementById('connectToSunoBtn');
  const btnText = document.getElementById('connectToSunoBtnText');
  if (!btn || !btnText) return;

  const extensionAvailable = await sunoService.isExtensionAvailable();

  if (extensionAvailable) {
    const tokenStatus = await sunoService.getTokenStatus();
    if (tokenStatus.hasToken) {
      btnText.textContent = 'Open Suno Library';
      btn.classList.add('btn-suno-ready');
      return;
    }
  }

  btnText.textContent = 'Connect To Suno';
  btn.classList.remove('btn-suno-ready');
}

// ─── Popup Management ────────────────────────────────────

function showPopup(state) {
  if (!isPopupCreated) {
    createPopup();
    isPopupCreated = true;
  }

  transitionTo(state);
  popupEl.style.display = '';
  lockBodyScroll();

  // Render Lucide icons inside popup
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [popupEl] });
  }

  startAutoDetection();
}

function closePopup() {
  if (!popupEl) return;

  stopAutoDetection();
  popupEl.style.display = 'none';
  currentState = null;
  unlockBodyScroll();
}

function createPopup() {
  const html = `
    <div id="sunoOnboarding" class="suno-onboarding" style="display: none;">
      <div class="suno-onboarding-backdrop"></div>
      <div class="suno-onboarding-content">
        <button class="suno-onboarding-close" aria-label="Close">&times;</button>

        <!-- Install State -->
        <div class="suno-onboarding-state" data-state="install">
          <div class="suno-onboarding-header">
            <i data-lucide="puzzle" class="suno-onboarding-icon"></i>
            <h3>Connect ShowLark to Suno</h3>
            <p>Import your library, generate AI playlists, and visualize your music — all from one place.</p>
          </div>
          <div class="suno-onboarding-features">
            <div class="suno-onboarding-feature">
              <i data-lucide="library"></i>
              <div>
                <strong>Browse Library</strong>
                <span>Import songs from your Suno library with filters and search</span>
              </div>
            </div>
            <div class="suno-onboarding-feature">
              <i data-lucide="sparkles"></i>
              <div>
                <strong>AI Generation</strong>
                <span>Generate songs with custom prompts and add them to your playlist</span>
              </div>
            </div>
            <div class="suno-onboarding-feature">
              <i data-lucide="shield-check"></i>
              <div>
                <strong>Privacy First</strong>
                <span>Your Suno tokens never leave your browser</span>
              </div>
            </div>
          </div>
          <a href="${CHROME_STORE_URL}" target="_blank" rel="noopener" class="suno-onboarding-cta">
            Add to Chrome — Free
          </a>
          <button class="suno-onboarding-fallback-link" id="sunoOnboardingAlreadyInstalled">
            Extension already installed?
          </button>
        </div>

        <!-- Connect State -->
        <div class="suno-onboarding-state" data-state="connect">
          <div class="suno-onboarding-header">
            <i data-lucide="log-in" class="suno-onboarding-icon"></i>
            <h3>Connect to Suno</h3>
            <p>Click the ShowLark extension icon, then "Connect to Suno" to grant access. Log into suno.com if needed.</p>
          </div>
          <a href="${SUNO_URL}" target="_blank" rel="noopener" class="suno-onboarding-cta">
            Open Suno
          </a>
          <div class="suno-onboarding-status">
            <button class="suno-onboarding-refresh-btn" id="sunoOnboardingRefresh" aria-label="Refresh connection status">
              <i data-lucide="refresh-cw"></i>
            </button>
            <span id="sunoOnboardingStatusText">Waiting for connection...</span>
          </div>
        </div>

        <!-- Success State -->
        <div class="suno-onboarding-state" data-state="success">
          <div class="suno-onboarding-header">
            <i data-lucide="check-circle" class="suno-onboarding-icon suno-onboarding-icon-success"></i>
            <h3>Connected!</h3>
            <p>Opening Suno Library...</p>
          </div>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', html);
  popupEl = document.getElementById('sunoOnboarding');

  // Backdrop click closes popup
  const backdrop = popupEl.querySelector('.suno-onboarding-backdrop');
  backdrop.addEventListener('click', closePopup);

  // Close button
  const closeBtn = popupEl.querySelector('.suno-onboarding-close');
  closeBtn.addEventListener('click', closePopup);

  // ESC key — stop propagation so drop modal ESC handler doesn't also fire
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popupEl && popupEl.style.display !== 'none') {
      e.stopPropagation();
      closePopup();
    }
  }, true); // capture phase to beat drop modal handler

  // "Extension already installed?" fallback
  const alreadyInstalledBtn = document.getElementById('sunoOnboardingAlreadyInstalled');
  alreadyInstalledBtn.addEventListener('click', () => transitionTo('connect'));

  // Refresh button in connect state
  const refreshBtn = document.getElementById('sunoOnboardingRefresh');
  refreshBtn.addEventListener('click', handleRefreshClick);
}

// ─── State Transitions ───────────────────────────────────

function transitionTo(state) {
  currentState = state;

  if (!popupEl) return;

  // Hide all state views, show the active one
  const states = popupEl.querySelectorAll('.suno-onboarding-state');
  states.forEach(el => {
    el.style.display = el.dataset.state === state ? '' : 'none';
  });

  // Re-render icons for the newly visible state
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [popupEl] });
  }

  // Success state → update button and auto-open Suno Manager after delay
  if (state === 'success') {
    updateButtonState();
    setTimeout(() => {
      closePopup();
      hideDropModal();
      openSunoManager('library');
    }, SUCCESS_DELAY_MS);
  }
}

// ─── Auto-Detection ──────────────────────────────────────

function startAutoDetection() {
  stopAutoDetection(); // prevent duplicates

  // Listen for extension content script loading (after fresh install)
  const extensionReadyHandler = async () => {
    if (!currentState) return;

    const tokenStatus = await sunoService.getTokenStatus();
    if (tokenStatus.hasToken) {
      transitionTo('success');
    } else if (currentState === 'install') {
      transitionTo('connect');
    }
  };

  document.addEventListener('showlarkExtensionReady', extensionReadyHandler);
  cleanupFns.push(() => document.removeEventListener('showlarkExtensionReady', extensionReadyHandler));

  // Listen for connection status changes (e.g., extension reconnect)
  const unsubscribe = sunoService.onConnectionStatusChange(async (status, details, connected) => {
    if (!currentState) return;

    if (connected) {
      const tokenStatus = await sunoService.getTokenStatus();
      if (tokenStatus.hasToken) {
        transitionTo('success');
      }
    }
  });

  cleanupFns.push(unsubscribe);

  // Poll for token acquisition while in 'connect' state.
  // Token changes (user logs into Suno, extension extracts cookie) have no
  // push notification path to the page, so we poll every 3 seconds.
  const pollInterval = setInterval(async () => {
    if (!currentState || currentState === 'success') return;

    const extensionAvailable = await sunoService.isExtensionAvailable();
    if (!extensionAvailable) return;

    const tokenStatus = await sunoService.getTokenStatus();
    if (tokenStatus.hasToken) {
      transitionTo('success');
    } else if (currentState === 'install') {
      transitionTo('connect');
    }
  }, 3000);

  cleanupFns.push(() => clearInterval(pollInterval));
}

function stopAutoDetection() {
  cleanupFns.forEach(fn => fn());
  cleanupFns = [];
}

// ─── Refresh Button ──────────────────────────────────────

async function handleRefreshClick() {
  const refreshBtn = document.getElementById('sunoOnboardingRefresh');
  const statusText = document.getElementById('sunoOnboardingStatusText');

  if (refreshBtn) refreshBtn.classList.add('spinning');
  if (statusText) statusText.textContent = 'Checking connection...';

  try {
    const extensionAvailable = await sunoService.isExtensionAvailable();

    if (!extensionAvailable) {
      if (statusText) statusText.textContent = 'Extension not detected. Try refreshing the page.';
      return;
    }

    const tokenStatus = await sunoService.getTokenStatus();
    if (tokenStatus.hasToken) {
      transitionTo('success');
    } else {
      if (statusText) statusText.textContent = 'Extension found — open the extension, connect to Suno, and log in.';
    }
  } catch {
    if (statusText) statusText.textContent = 'Connection check failed. Try again.';
  } finally {
    if (refreshBtn) refreshBtn.classList.remove('spinning');
  }
}
